/**
 * Lecture avec forme : un fichier Word rendu en texte, une ligne par
 * paragraphe, chaque ligne précédée de la forme du paragraphe.
 *
 * ## Pourquoi elle existe
 *
 * Un avocat demande « mets mon acte en forme comme ce modèle ». Les lectures de
 * texte ne disent rien de la forme, et les modèles d'avocat n'emploient presque
 * jamais de styles nommés : la forme y est posée à la main, paragraphe par
 * paragraphe. Mesuré le 2026-10-11 sur une assignation de 230 paragraphes :
 * sans cette lecture, l'agent change la forme de 3 paragraphes et annonce une
 * conformité fausse ; avec elle, il met en forme les 219 paragraphes non vides
 * sans changer un mot.
 *
 * ## Ce qu'elle rend
 *
 * La forme RÉSOLUE par la cascade de `lecteurDeForme.ts` (police, taille, gras,
 * alignement, retraits, espacements, bordures, fond du paragraphe), et ce que
 * le fichier pose à la main sur le paragraphe ou la cellule et que la cascade
 * ne résout pas : fond de cellule, taquets, saut de page, type de liste,
 * structure des tableaux. En-têtes, pieds et notes n'y figurent pas.
 *
 * ⚠️ Le parcours du corps suit celui de `paragraphesDansLOrdre` : c'est lui qui
 * apparie chaque paragraphe du fichier à sa forme résolue. Un écart de compte
 * est levé, jamais rendu.
 */
import { checkDocxArchiveBounds, isWord, parseXml, type XmlNode } from '@hector/docx-integrity';
import { CANVAS_PARAGRAPH_BORDER_SIDES, readTextListPrefix, type CanvasParagraphBorderSide } from '@hector/shared-types';
import JSZip from 'jszip';
import { allume, attribut, enfant } from './cascadeDeForme';
import {
  corpsAvecSesCellules,
  LectureDeFormeImpossible,
  lireLaForme,
  lireLeFondPose,
  type FormeDeParagraphe,
  type FormeDePassage,
  type PageDeSection,
  type Reglage,
  type TraitDeBordure,
} from './lecteurDeForme';
import { lignesDuTableau, styleDuTableau } from './styleDeTableau';
import { huitiemesEnPt, twipsEnCm } from './unitesDeForme';

/**
 * Taille d'une page de lecture, en caractères. Celle de `read_document`
 * (`READ_DOCUMENT_WINDOW_LENGTH`, `apps/backend/services/documents/documentReadLimits.ts`) :
 * l'agent pagine les deux lectures au même grain. Mesuré le 2026-10-11 : un
 * modèle de 157 paragraphes rend 40 000 caractères, un acte de 219 en rend 58 000.
 */
export const TAILLE_DE_PAGE_DE_LECTURE = 60_000;

/** Une page de la lecture avec forme. Les rangs comptent les paragraphes non vides, à partir de 1. */
export interface PageDeLecture {
  /** La légende, la page du document, puis les paragraphes de `premier` à `dernier`. */
  texte: string;
  totalParagraphes: number;
  /** Rang du premier paragraphe rendu ; 0 quand le document n'en a aucun. */
  premier: number;
  dernier: number;
  /** Rang où reprendre, `null` quand tout est rendu. */
  suivant: number | null;
}

/** Une page rendue avec les identifiants de bloc ({@link lireAvecForme}, `identifiants`). */
export interface PageDeLectureAvecIdentifiants extends PageDeLecture {
  /**
   * Les identifiants rendus `[rang · id …]` sur un paragraphe du CORPS de cette page, dans l'ordre : ceux qu'une
   * écriture de texte adresse. Ni un paragraphe de cellule (son texte se change par passage), ni un paragraphe
   * « sans identifiant » ou « identifiant en double ».
   */
  identifiantsDuCorps: string[];
}

/** `depuis` ne désigne aucun paragraphe du document. Porte le total, pour que l'appelant dise la borne sans relire le fichier. */
export class RangHorsDuDocument extends Error {
  constructor(readonly depuis: number, readonly totalParagraphes: number) {
    super(`lireAvecForme: depuis=${depuis} is not a paragraph rank of this document, which has ${totalParagraphes} non-empty paragraph(s)${totalParagraphes === 0 ? '' : ` (ranks 1 to ${totalParagraphes})`}.`);
    this.name = 'RangHorsDuDocument';
  }
}

/** Un paragraphe rendu et les lignes de contexte qui le précèdent (tableau, cellule, saut de page). */
interface Groupe {
  rang: number;
  lignes: string[];
  /** L'identifiant que le repère montre, quand le paragraphe est du corps et que cet identifiant le vise seul. */
  identifiantDuCorps: string | null;
}

type FormeDeTexte = Pick<FormeDePassage, 'fontFamily' | 'fontSizePt' | 'bold' | 'italic' | 'underline' | 'color' | 'highlight' | 'couleurDeTheme'>;

/** La couleur du texte telle que l'avocat la voit : celle que la cascade résout, sinon le repli d'une couleur de thème. */
const couleurDuTexte = (forme: Pick<FormeDePassage, 'color' | 'couleurDeTheme'>): string | undefined => couleurDe(forme.color.lisible ? forme.color.valeur : forme.couleurDeTheme);

const ALIGNEMENTS: Record<string, string> = { left: 'aligné à gauche', center: 'centré', right: 'aligné à droite', justify: 'justifié' };
const COTES: Record<CanvasParagraphBorderSide, string> = { top: 'haut', left: 'gauche', bottom: 'bas', right: 'droite' };
const COTES_DE_TABLEAU: ReadonlyArray<[string, string]> = [['top', 'haut'], ['left', 'gauche'], ['bottom', 'bas'], ['right', 'droite'], ['insideH', 'entre les lignes'], ['insideV', 'entre les colonnes']];
const TAQUETS: Record<string, string> = { left: 'gauche', center: 'centré', right: 'droite', decimal: 'décimal', start: 'gauche', end: 'droite' };
const POINTS_DE_SUITE: Record<string, string> = { dot: 'points de suite', hyphen: 'tirets de suite', underscore: 'trait de suite', middleDot: 'points médians de suite' };
const ALIGNEMENTS_VERTICAUX: Record<string, string> = { top: 'en haut', center: 'au milieu', bottom: 'en bas' };
/** Les formats de numéro de liste (`ST_NumberFormat`) qu'un acte emploie, en mots d'avocat ; les autres sont rendus par leur nom Word. */
const FORMATS_DE_LISTE: Record<string, string> = {
  bullet: 'puces',
  decimal: 'numéros 1, 2, 3',
  upperRoman: 'chiffres romains I, II, III',
  lowerRoman: 'chiffres romains i, ii, iii',
  upperLetter: 'lettres A, B, C',
  lowerLetter: 'lettres a, b, c',
};
const SANS_COULEUR: ReadonlySet<string> = new Set(['', 'auto', 'none', '000000', '#000000']);

const valeur = <Valeur>(reglage: Reglage<Valeur>): Valeur | undefined => (reglage.lisible ? reglage.valeur : undefined);
const arrondi = (nombre: number, decimales = 1): string => String(Math.round(nombre * 10 ** decimales) / 10 ** decimales);
const cm = (twips: number | null | undefined): string => (twips === null || twips === undefined ? '?' : twipsEnCm(twips).toFixed(1));
const entier = (texte: string | undefined): number | undefined => {
  const nombre = texte === undefined ? Number.NaN : Number(texte);
  return Number.isFinite(nombre) ? nombre : undefined;
};
/** Un fond en mots : rien sans fond, « fond ? » quand il ne se lit pas. Le noir y est une couleur, contrairement au texte. */
const fondEnMots = (fond: Reglage<string>): string | undefined => {
  if (!fond.lisible) return 'fond ?';
  return fond.valeur === 'none' ? undefined : `fond ${fond.valeur}`;
};
const couleurDe = (texte: string | undefined): string | undefined => (texte === undefined || SANS_COULEUR.has(texte) ? undefined : texte.replace(/^#/, '').toUpperCase());

function pageEnMots(page: PageDeSection | undefined): string {
  if (page === undefined) return 'page non lue';
  const { top, bottom, left, right } = page.margesTwips;
  return `page ${cm(page.largeurTwips)} × ${cm(page.hauteurTwips)} cm${page.orientation === 'landscape' ? ' (paysage)' : ''}, `
    + `marges haut ${cm(top)} cm, bas ${cm(bottom)} cm, gauche ${cm(left)} cm, droite ${cm(right)} cm`;
}

/** Un trait de bordure du fichier (`w:top`, `w:insideH`…) : `null` quand il est absent ou éteint. */
function traitEnMots(noeud: XmlNode | undefined): string | null {
  const type = attribut(noeud, 'val');
  if (type === undefined || type === 'nil' || type === 'none') return null;
  const epaisseur = entier(attribut(noeud, 'sz'));
  const couleur = couleurDe(attribut(noeud, 'color'));
  return [type, epaisseur === undefined ? undefined : `${arrondi(huitiemesEnPt(epaisseur), 2)} pt`, couleur === undefined ? undefined : `couleur ${couleur}`].filter(Boolean).join(' ');
}

/**
 * Les bordures qu'un tableau (`w:tblBorders`) ou une cellule (`w:tcBorders`) pose, côté par côté ; les côtés identiques
 * sont réunis. « aucune bordure » quand tous les côtés posés sont annulés et qu'ils sont au moins `cotes` (les quatre
 * d'une cellule, les six d'un tableau) : c'est ce que rend une cellule dont `format_paragraphs` a retiré le cadre
 * (`cellBorder:"none"`), et un tableau aux six traits éteints.
 */
function borduresDeTableauEnMots(bordures: XmlNode | undefined, cotes: number): string | undefined {
  if (bordures === undefined) return undefined;
  const parTrait = new Map<string, string[]>();
  for (const [cote, nom] of COTES_DE_TABLEAU) {
    const noeud = bordures.children.find((candidat) => isWord(candidat, cote) || (cote === 'left' && isWord(candidat, 'start')) || (cote === 'right' && isWord(candidat, 'end')));
    if (noeud === undefined) continue;
    const trait = traitEnMots(noeud) ?? 'aucune';
    parTrait.set(trait, [...(parTrait.get(trait) ?? []), nom]);
  }
  if (parTrait.size === 0) return undefined;
  if (parTrait.size === 1 && (parTrait.get('aucune')?.length ?? 0) >= cotes) return 'aucune bordure';
  return `bordures ${[...parTrait].map(([trait, noms]) => `${noms.join(', ')} : ${trait}`).join(' ; ')}`;
}

/**
 * Les bordures d'un paragraphe telles que la cascade les résout, côté par côté. Quatre côtés identiques font un cadre.
 * Un trait à couleur de thème n'a pas de trait lisible, mais son épaisseur l'est : c'est elle qui est dite.
 */
function borduresDeParagrapheEnMots({ traitsDeBordure: traits, bordures: epaisseurs }: Pick<FormeDeParagraphe, 'traitsDeBordure' | 'bordures'>): string | undefined {
  const dire = (trait: TraitDeBordure): string => {
    const couleur = couleurDe(trait.couleur);
    return `${trait.type} ${arrondi(huitiemesEnPt(trait.huitiemes), 2)} pt${couleur === undefined ? '' : ` couleur ${couleur}`}${trait.ecart ? ` distance texte ${arrondi(trait.ecart, 2)} pt` : ''}`;
  };
  const poses = CANVAS_PARAGRAPH_BORDER_SIDES.flatMap((cote): Array<[CanvasParagraphBorderSide, string]> => {
    const trait = traits[cote];
    if (trait.lisible) return trait.valeur === null ? [] : [[cote, dire(trait.valeur)]];
    const epaisseur = epaisseurs[cote];
    if (epaisseur.lisible) return epaisseur.valeur === 0 ? [] : [[cote, `${arrondi(epaisseur.valeur, 2)} pt`]];
    return [[cote, '?']];
  });
  if (poses.length === 0) return undefined;
  if (poses.length === CANVAS_PARAGRAPH_BORDER_SIDES.length && poses.every(([, trait]) => trait === poses[0]![1])) return `cadre ${poses[0]![1]}`;
  return poses.map(([cote, trait]) => `bordure ${COTES[cote]} ${trait}`).join(', ');
}

/** Format et motif de chaque niveau de chaque liste du fichier (`numbering.xml`), par `numId` puis `ilvl`. Les surcharges de niveau ne sont pas lues. */
function lireLesListes(racine: XmlNode | undefined): Map<string, Map<string, { format: string | undefined; motif: string | undefined }>> {
  const modeles = new Map<string, Map<string, { format: string | undefined; motif: string | undefined }>>();
  for (const noeud of racine?.children ?? []) {
    const id = isWord(noeud, 'abstractNum') ? attribut(noeud, 'abstractNumId') : undefined;
    if (id === undefined) continue;
    modeles.set(id, new Map(noeud.children.flatMap((niveau) => {
      const rang = isWord(niveau, 'lvl') ? attribut(niveau, 'ilvl') : undefined;
      return rang === undefined ? [] : [[rang, { format: attribut(enfant(niveau, 'numFmt'), 'val'), motif: attribut(enfant(niveau, 'lvlText'), 'val') }] as const];
    })));
  }
  const listes = new Map<string, Map<string, { format: string | undefined; motif: string | undefined }>>();
  for (const noeud of racine?.children ?? []) {
    const id = isWord(noeud, 'num') ? attribut(noeud, 'numId') : undefined;
    const modele = id === undefined ? undefined : modeles.get(attribut(enfant(noeud, 'abstractNumId'), 'val') ?? '');
    if (id !== undefined && modele !== undefined) listes.set(id, modele);
  }
  return listes;
}

/** Vrai si un descendant du nœud porte ce nom `w:`, sans entrer dans un paragraphe imbriqué (zone de texte). */
function porte(noeud: XmlNode, garde: (candidat: XmlNode) => boolean): boolean {
  return noeud.children.some((candidat) => garde(candidat) || (!isWord(candidat, 'p') && porte(candidat, garde)));
}

const estAllume = (noeud: XmlNode | undefined): boolean => noeud !== undefined && allume(attribut(noeud, 'val'), true);
const estUneImage = (noeud: XmlNode): boolean => noeud.local === 'drawing' || noeud.local === 'pict' || noeud.local === 'object';

/**
 * Le texte des zones de texte ancrées dans un paragraphe, un morceau par paragraphe de zone. Word écrit chaque zone
 * deux fois (`mc:Choice` puis `mc:Fallback`) : le repli est ignoré.
 */
function textesDesZones(noeud: XmlNode): string[] {
  const texteDe = (interieur: XmlNode): string => (isWord(interieur, 't') ? interieur.text : interieur.children.map(texteDe).join(''));
  const dans = (candidat: XmlNode): string[] => {
    if (candidat.local === 'Fallback') return [];
    if (isWord(candidat, 'txbxContent')) return candidat.children.filter((p) => isWord(p, 'p')).map((p) => texteDe(p).replace(/\s+/g, ' ').trim()).filter((texte) => texte !== '');
    return isWord(candidat, 'p') ? [] : candidat.children.flatMap(dans);
  };
  return noeud.children.flatMap(dans);
}

const estUnSautDePage = (noeud: XmlNode): boolean => isWord(noeud, 'br') && attribut(noeud, 'type') === 'page';

/** La forme de texte qui couvre le plus de caractères du paragraphe : celle que disent les accolades. */
function formeDominante(passages: readonly FormeDePassage[]): FormeDeTexte {
  const cle = (passage: FormeDePassage): string => JSON.stringify([passage.fontFamily, passage.fontSizePt, passage.bold, passage.italic, passage.underline, passage.color, passage.highlight].map((reglage) => (reglage.lisible ? reglage.valeur : null)).concat(passage.couleurDeTheme ?? null));
  const poids = new Map<string, { passage: FormeDePassage; caracteres: number }>();
  for (const passage of passages) {
    const connu = poids.get(cle(passage)) ?? { passage, caracteres: 0 };
    connu.caracteres += passage.texte.trim().length;
    poids.set(cle(passage), connu);
  }
  return [...poids.values()].sort((a, b) => b.caracteres - a.caracteres)[0]!.passage;
}

/** Un passage dans la ligne : son texte, marqué là où sa forme s'écarte de celle du paragraphe. */
function passageEnTexte(passage: FormeDePassage, dominante: FormeDeTexte): string {
  const [, avant = '', coeur = '', apres = ''] = /^(\s*)(.*?)(\s*)$/s.exec(passage.texte.replace(/\t/g, ' → ').replace(/\n/g, ' ⏎ ').replace(/\s+/g, ' ')) ?? [];
  if (coeur === '') return avant + apres;
  let marque = coeur;
  const bascules: Array<['bold' | 'italic' | 'underline', string, string]> = [['bold', '**', 'non gras'], ['italic', '*', 'non italique'], ['underline', '__', 'non souligné']];
  for (const [nom, balise, negation] of bascules) {
    const ici = valeur(passage[nom]) === true;
    if (ici === (valeur(dominante[nom]) === true)) continue;
    marque = ici ? `${balise}${marque}${balise}` : `[${negation}: ${marque}]`;
  }
  const ecarts: string[] = [];
  const taille = valeur(passage.fontSizePt);
  if (taille !== undefined && taille !== valeur(dominante.fontSizePt)) ecarts.push(`${arrondi(taille)} pt`);
  const police = valeur(passage.fontFamily);
  if (police !== undefined && police !== valeur(dominante.fontFamily)) ecarts.push(police);
  const couleur = couleurDuTexte(passage);
  if (couleur !== couleurDuTexte(dominante)) ecarts.push(`couleur ${couleur ?? 'automatique'}`);
  const surlignage = couleurDe(valeur(passage.highlight));
  if (surlignage !== couleurDe(valeur(dominante.highlight))) ecarts.push(surlignage === undefined ? 'non surligné' : `surligné ${surlignage}`);
  if (porteALaMain(passage, 'caps') || porteALaMain(passage, 'smallCaps')) ecarts.push(porteALaMain(passage, 'caps') ? 'capitales par attribut' : 'petites capitales');
  return `${avant}${marque}${ecarts.length === 0 ? '' : `⟨${ecarts.join(', ')}⟩`}${apres}`;
}

const porteALaMain = (passage: FormeDePassage, element: string): boolean => (passage.elementsALaMain ?? []).includes(element);

export const LEGENDE_DE_LA_LECTURE = [
  'Une ligne par paragraphe non vide : [rang] {forme du paragraphe} texte entier.',
  'Entre accolades : la forme de la plus grande part du texte, puis celle du paragraphe. Un réglage absent vaut zéro ou non (pas de gras, pas de retrait, pas d\'espacement, interligne simple, pas de bordure, pas de fond, pas de liste). « ? » : valeur que le fichier ne permet pas de lire. « lignes vides avant : N » compte les paragraphes vides qui précèdent.',
  'Dans le texte, un passage qui s\'écarte de la forme du paragraphe est marqué : **gras**, *italique*, __souligné__, [non gras: …], [non italique: …], [non souligné: …], et ⟨…⟩ après lui pour une taille, une police, une couleur, un surlignage ou des capitales par attribut. « → » est une tabulation, « ⏎ » un retour à la ligne dans le paragraphe.',
  'Un tableau est rendu à sa place : une ligne [tableau Tn …], puis chaque cellule (Tn Lx Cy …) suivie de ses paragraphes, en retrait. Les bordures d\'une cellule l\'emportent sur celles de son tableau.',
  'Le texte d\'une zone de texte est rendu sur le paragraphe qui l\'ancre, entre [zone de texte : …], sans sa forme. [image] marque une image. En-têtes, pieds de page et notes sont hors de cette lecture.',
  'Écrire dans l\'acte ouvert ce qui est lu ici :',
  '- « retrait gauche 8 cm » → format_paragraphs indentLeftCm:8 (droit : indentRightCm, première ligne : indentFirstLineCm) ; « espace avant 6 pt » → spaceBeforePt:6 (après : spaceAfterPt).',
  '- « bordure bas 0.5 pt » → format_paragraphs border:"bottom", borderWidthPt:0.5 (haut : top, gauche : left, droite : right, plusieurs côtés joints par « + », « cadre » : "box").',
  '- « fond 554E94 » sur un paragraphe → format_paragraphs shading:"554E94".',
  '- fond ou cadre d\'une cellule (Tn Lx Cy) → format_paragraphs cellShading, cellBorder, en visant un paragraphe de cette cellule ; cellBorder:"none" retire le cadre, qu\'il vienne de la cellule, du tableau ou de son style, et la cellule est alors lue « aucune bordure ».',
  '- [saut de page] → insert_page_break avant le paragraphe qui le suit.',
] as const;

/**
 * Ce que l'agent fait d'un identifiant lu. WHY « de forme et de texte » : mesuré dans la vraie application le
 * 2026-10-12 (deux passages), l'agent qui devait recomposer une page de garde ne tentait aucune écriture de texte sur
 * ces identifiants. Une cellule est nommée à part : son paragraphe porte un identifiant que les écritures de forme
 * visent, et que `rewrite_block` refuse.
 */
const LEGENDE_DE_L_IDENTIFIANT = '« id » est l\'identifiant de bloc du paragraphe : la valeur à passer telle quelle aux écritures de forme et de texte qui visent ce paragraphe (nodeIds, nodeId, from, to). Le texte d\'un paragraphe de cellule (Tn Lx Cy) se change par replace_text. « sans identifiant » et « identifiant en double » marquent un paragraphe que ces écritures n\'atteignent pas.';

/**
 * La légende quand chaque ligne porte l'identifiant de bloc ({@link lireAvecForme}, `identifiants`) :
 * la première ligne montre le repère tel qu'il est rendu, et une phrase dit à quoi sert l'identifiant.
 */
const LEGENDE_AVEC_IDENTIFIANTS = [
  'Une ligne par paragraphe non vide : [rang · id identifiant] {forme du paragraphe} texte entier.',
  LEGENDE_DE_L_IDENTIFIANT,
  ...LEGENDE_DE_LA_LECTURE.slice(1),
] as const;

/**
 * La légende lue par l'agent Word. WHY : `insert_page_break` est un outil Canvas ; dans le catalogue Word il
 * n'existe pas, et la ligne de la légende Canvas y produisait un appel d'outil inconnu (revue #2172).
 */
const LEGENDE_POUR_WORD = LEGENDE_DE_LA_LECTURE.map((ligne) => (ligne.startsWith('- [saut de page]')
  ? "- [saut de page] → l'agent Word n'a pas d'outil de saut de page : signale-le à l'avocat (Insertion, Saut de page) au lieu de l'imiter par des lignes vides."
  : ligne));

/**
 * Rend la lecture avec forme d'un fichier Word, par page de paragraphes entiers.
 *
 * @param octets Le fichier .docx.
 * @param options.depuis Rang du premier paragraphe à rendre (défaut 1).
 * @param options.tailleMax Taille de la page en caractères, hors légende (défaut {@link TAILLE_DE_PAGE_DE_LECTURE}). Un paragraphe plus long qu'elle est rendu seul, en entier.
 * @param options.identifiants Vrai pour rendre l'identifiant de bloc de chaque paragraphe (`w14:paraId`) dans son repère : `[12 · id 5953B52C]`.
 *   Pour le document que l'agent écrit, pas pour un modèle qu'il lit : un identifiant de modèle ne vise rien.
 * @param options.editeur `word` pour l'agent de l'add-in Word : la légende y renvoie à ses outils, pas à ceux du Canvas.
 * @throws LectureDeFormeImpossible Le fichier n'est pas un .docx lisible.
 * @throws RangHorsDuDocument `depuis` n'est pas un rang du document.
 */
export async function lireAvecForme(octets: Uint8Array, options: { depuis?: number; tailleMax?: number; identifiants: true }): Promise<PageDeLectureAvecIdentifiants>;
export async function lireAvecForme(octets: Uint8Array, options?: { depuis?: number; tailleMax?: number; identifiants?: boolean; editeur?: 'canvas' | 'word' }): Promise<PageDeLecture>;
export async function lireAvecForme(octets: Uint8Array, options: { depuis?: number; tailleMax?: number; identifiants?: boolean; editeur?: 'canvas' | 'word' } = {}): Promise<PageDeLecture | PageDeLectureAvecIdentifiants> {
  // Le fichier vient d'un tiers (modèle, pièce, pièce jointe) : sans cette garde, JSZip décompresse une archive forgée sans plafond.
  const bornes = checkDocxArchiveBounds(octets);
  if (bornes.status === 'rejected') {
    throw new LectureDeFormeImpossible(`the ${octets.byteLength} bytes are not a Word (.docx) archive within the control ceilings (${bornes.issues[0]?.code ?? 'DOCX_ARCHIVE_INVALID'}). Nothing was read.`);
  }
  const acte = await lireLaForme(octets);
  const archive = await JSZip.loadAsync(octets);
  const partie = async (chemin: string): Promise<XmlNode | undefined> => {
    const fichier = archive.file(chemin);
    return fichier ? parseXml(await fichier.async('string')) : undefined;
  };
  // `lireLaForme` vient de lire ce corps : il existe et se parse.
  const corps = enfant(await partie('word/document.xml'), 'body')!;
  const listes = lireLesListes(await partie('word/numbering.xml').catch(() => undefined));

  // Un identifiant porté par deux paragraphes n'en vise aucun : la vue visée par les écritures en décide, elle seule (`corpsAvecSesCellules`).
  const enDouble = new Set(options.identifiants === true ? corpsAvecSesCellules(acte).paragraphes.flatMap((vise) => vise.identifiantEnDouble ?? []) : []);
  const repere = (rang: number, nodeId: string | null): string => {
    if (options.identifiants !== true) return `[${rang}]`;
    if (nodeId === null) return `[${rang} · sans identifiant]`;
    return enDouble.has(nodeId) ? `[${rang} · identifiant en double]` : `[${rang} · id ${nodeId}]`;
  };

  const groupes: Groupe[] = [];
  let enAttente: string[] = [];
  let vides = 0;
  let duCorps = 0;
  let deTableau = 0;
  let tableaux = 0;
  let section = 0;

  const rendreLeParagraphe = (noeud: XmlNode, forme: FormeDeParagraphe, profondeur: number): void => {
    const marge = '  '.repeat(profondeur);
    const pPr = enfant(noeud, 'pPr');
    const passages = forme.passages.filter((passage) => passage.texte.length > 0);
    const zones = textesDesZones(noeud);
    const image = zones.length === 0 && porte(noeud, estUneImage);
    if (porte(noeud, estUnSautDePage)) enAttente.push(`${marge}[saut de page]`);
    const finDeSection = enfant(pPr, 'sectPr') !== undefined;
    const texteComplet = passages.map((passage) => passage.texte).join('');
    const texteNu = texteComplet.trim();
    if (texteNu === '' && !image && zones.length === 0) {
      vides += 1;
    } else {
      const reglages: string[] = [];
      const dominante = passages.length > 0 ? formeDominante(passages) : undefined;
      if (dominante !== undefined) {
        const taille = valeur(dominante.fontSizePt);
        reglages.push(`${valeur(dominante.fontFamily) ?? 'police ?'} ${taille === undefined ? '? pt' : `${arrondi(taille)} pt`}`);
        if (valeur(dominante.bold) === true) reglages.push('gras');
        if (valeur(dominante.italic) === true) reglages.push('italique');
        if (valeur(dominante.underline) === true) reglages.push('souligné');
        const couleur = couleurDuTexte(dominante);
        if (couleur !== undefined) reglages.push(`couleur ${couleur}`);
        const surlignage = couleurDe(valeur(dominante.highlight));
        if (surlignage !== undefined) reglages.push(`surligné ${surlignage}`);
        const lettres = texteNu.replace(/[^\p{L}]/gu, '');
        if (lettres.length >= 3 && lettres === lettres.toUpperCase() && lettres !== lettres.toLowerCase()) reglages.push('capitales tapées');
      }
      const { alignment, indentLeftCm, indentRightCm, indentFirstLineCm, spaceBeforePt, spaceAfterPt, lineSpacing } = forme.paragraphe;
      reglages.push(alignment.lisible ? ALIGNEMENTS[alignment.valeur] ?? alignment.valeur : 'alignement ?');
      const mesures: Array<[Reglage<number>, string, string, number]> = [
        [indentLeftCm, 'retrait gauche', 'cm', 1], [indentRightCm, 'retrait droit', 'cm', 1], [indentFirstLineCm, 'première ligne', 'cm', 1],
        [spaceBeforePt, 'espace avant', 'pt', 0], [spaceAfterPt, 'espace après', 'pt', 0],
      ];
      for (const [reglage, nom, unite, decimales] of mesures) {
        if (!reglage.lisible) reglages.push(`${nom} ?`);
        else if (reglage.valeur !== 0) reglages.push(`${nom} ${arrondi(reglage.valeur, decimales)} ${unite}`);
      }
      if (!lineSpacing.lisible) reglages.push('interligne ?');
      else if (lineSpacing.valeur !== 1) reglages.push(`interligne ${arrondi(lineSpacing.valeur, 2)}`);
      const bordures = borduresDeParagrapheEnMots(forme);
      if (bordures !== undefined) reglages.push(bordures);
      const fond = fondEnMots(forme.fond);
      if (fond !== undefined) reglages.push(fond);
      if (forme.numerotation !== 'aucune') {
        const marque = enfant(pPr, 'numPr');
        const rang = attribut(enfant(marque, 'ilvl'), 'val') ?? '0';
        const niveau = listes.get(attribut(enfant(marque, 'numId'), 'val') ?? '')?.get(rang);
        const format = niveau?.format === undefined ? undefined : FORMATS_DE_LISTE[niveau.format] ?? niveau.format;
        // Un motif de puce est souvent un caractère de police de symboles, illisible hors de Word : seul un motif de numéro est rendu.
        const motif = niveau?.format === 'bullet' || niveau?.motif === undefined ? '' : ` « ${niveau.motif} »`;
        reglages.push(format === undefined
          ? `liste automatique${forme.numerotation === 'du_style' ? ' portée par le style' : ''}`
          : `liste automatique niveau ${Number(rang) + 1}, ${format}${motif}`);
      } else {
        // Les puces manuelles font partie du texte : leur préfixe exact doit être visible avant la réécriture,
        // même si l'affichage du paragraphe retire ses blancs de bord. Une liste native garde sa propre lecture.
        const prefixe = readTextListPrefix(texteComplet);
        if (prefixe !== undefined) reglages.push(`préfixe saisi dans le texte ${JSON.stringify(prefixe.prefix)} ; à conserver lors d’une réécriture du contenu`);
      }
      for (const taquet of enfant(pPr, 'tabs')?.children ?? []) {
        const sorte = attribut(taquet, 'val');
        const position = entier(attribut(taquet, 'pos'));
        if (!isWord(taquet, 'tab') || sorte === 'clear' || position === undefined) continue;
        const suite = POINTS_DE_SUITE[attribut(taquet, 'leader') ?? ''];
        reglages.push(`taquet ${TAQUETS[sorte ?? 'left'] ?? sorte} à ${cm(position)} cm${suite === undefined ? '' : ` avec ${suite}`}`);
      }
      if (estAllume(enfant(pPr, 'pageBreakBefore'))) reglages.push('saut de page avant');
      if (estAllume(enfant(pPr, 'keepNext'))) reglages.push('solidaire du paragraphe suivant');
      if (forme.styleNom !== null && !/^normal$/i.test(forme.styleNom)) reglages.push(`style nommé « ${forme.styleNom} »`);
      if (vides > 0) reglages.push(`lignes vides avant : ${vides}`);
      vides = 0;
      const texte = dominante === undefined ? '' : passages.map((passage) => passageEnTexte(passage, dominante)).join('').trim();
      const rang = groupes.length + 1;
      const identifiantDuCorps = profondeur === 0 && forme.nodeId !== null && !enDouble.has(forme.nodeId) ? forme.nodeId : null;
      groupes.push({ rang, identifiantDuCorps, lignes: [...enAttente, `${marge}${repere(rang, forme.nodeId)} {${reglages.join(' · ')}} ${[texte, image ? '[image]' : '', zones.length === 0 ? '' : `[zone de texte : ${zones.join(' / ')}]`].filter(Boolean).join(' ')}`] });
      enAttente = [];
    }
    if (finDeSection) {
      section += 1;
      enAttente.push(`${marge}[nouvelle section : ${pageEnMots(acte.sections[section])}]`);
    }
  };

  const rendreLeTableau = (tableau: XmlNode, profondeur: number): void => {
    tableaux += 1;
    const nom = `T${tableaux}`;
    const marge = '  '.repeat(profondeur);
    const lignes = lignesDuTableau(tableau);
    const proprietes = enfant(tableau, 'tblPr');
    const colonnes = (enfant(tableau, 'tblGrid')?.children ?? []).filter((colonne) => isWord(colonne, 'gridCol')).map((colonne) => entier(attribut(colonne, 'w')));
    const style = styleDuTableau(tableau);
    const bordures = borduresDeTableauEnMots(enfant(proprietes, 'tblBorders'), COTES_DE_TABLEAU.length);
    const alignement = attribut(enfant(proprietes, 'jc'), 'val');
    enAttente.push(`${marge}[tableau ${nom} · ${lignes.length} lignes × ${colonnes.length || Math.max(0, ...lignes.map(({ cellules }) => cellules.length))} colonnes${[
      colonnes.length === 0 ? undefined : `largeurs des colonnes ${colonnes.map((largeur) => cm(largeur)).join(' / ')} cm`,
      alignement === undefined ? undefined : `tableau ${ALIGNEMENTS[alignement === 'both' ? 'justify' : alignement] ?? alignement}`,
      bordures ?? (style === undefined ? 'aucune bordure posée' : 'bordures portées par le style du tableau, non lues'),
      style === undefined ? undefined : `style de tableau « ${style} »`,
    ].filter(Boolean).map((part) => ` · ${part}`).join('')}]`);
    for (const [rangDeLigne, { cellules }] of lignes.entries()) {
      for (const [rangDeCellule, cellule] of cellules.entries()) {
        const tcPr = enfant(cellule, 'tcPr');
        const largeur = attribut(enfant(tcPr, 'tcW'), 'type') === 'dxa' ? entier(attribut(enfant(tcPr, 'tcW'), 'w')) : undefined;
        const fusion = entier(attribut(enfant(tcPr, 'gridSpan'), 'val'));
        const verticale = enfant(tcPr, 'vMerge');
        const pose = lireLeFondPose(enfant(tcPr, 'shd'));
        const fond = pose === undefined ? undefined : fondEnMots('valeur' in pose ? { lisible: true, valeur: pose.valeur, origine: 'direct' } : { lisible: false, raison: pose.nonLisible });
        const borduresDeCellule = borduresDeTableauEnMots(enfant(tcPr, 'tcBorders'), CANVAS_PARAGRAPH_BORDER_SIDES.length);
        const vertical = ALIGNEMENTS_VERTICAUX[attribut(enfant(tcPr, 'vAlign'), 'val') ?? ''];
        // Une case sans bordure à elle peut être encadrée par son tableau ou son style : sans le dire sur SA ligne, l'agent
        // ne voit pas de cadre à retirer (passage réel du 2026-10-12 : fond posé, cadre du titre laissé). Le cadre résolu
        // est porté par le premier paragraphe de la case, le prochain que le parcours va apparier.
        const cadreDonneParLeTableau = (): string | undefined => {
          const cadre = isWord(cellule.children.find((enfantDeCase) => !isWord(enfantDeCase, 'tcPr')) ?? cellule, 'p') ? acte.paragraphesDeTableau[deTableau]?.cellule?.cadre : undefined;
          if (cadre === undefined) return undefined;
          const traces = CANVAS_PARAGRAPH_BORDER_SIDES.filter((cote) => { const trait = cadre[cote]; return !trait.lisible || trait.valeur > 0; });
          if (traces.length === 0) return undefined;
          const epaisseurs = new Set(traces.map((cote) => { const trait = cadre[cote]; return trait.lisible ? `${arrondi(trait.valeur, 2)} pt` : '?'; }));
          return `cadre donné par le tableau : ${traces.map((cote) => COTES[cote]).join(', ')}${epaisseurs.size === 1 ? ` ${[...epaisseurs][0]}` : ''}`;
        };
        const enTete = `${marge}  (${[
          `${nom} L${rangDeLigne + 1} C${rangDeCellule + 1}`,
          largeur === undefined ? undefined : `largeur ${cm(largeur)} cm`,
          fusion !== undefined && fusion > 1 ? `fusion de ${fusion} colonnes` : undefined,
          verticale === undefined ? undefined : attribut(verticale, 'val') === 'restart' ? 'début de fusion verticale' : 'suite de fusion verticale',
          fond,
          borduresDeCellule ?? cadreDonneParLeTableau(),
          vertical === undefined ? undefined : `texte ${vertical}`,
        ].filter(Boolean).join(' · ')}`;
        const avant = groupes.length;
        const rangDeLEnTete = enAttente.push(`${enTete})`) - 1;
        // Les lignes vides se comptent dans la cellule : une cellule vide n'en donne pas au paragraphe suivant.
        vides = 0;
        parcourir(cellule, profondeur + 1);
        vides = 0;
        // Une cellule sans texte n'a rendu aucun paragraphe : son en-tête attend encore, et le dit.
        if (groupes.length === avant) enAttente[rangDeLEnTete] = `${enTete} · vide)`;
      }
    }
    enAttente.push(`${marge}[fin du tableau ${nom}]`);
  };

  /** Même ordre que `paragraphesDansLOrdre` : paragraphes, tableaux par cellule, enveloppes traversées. */
  function parcourir(conteneur: XmlNode, profondeur: number): void {
    for (const candidat of conteneur.children) {
      if (isWord(candidat, 'p')) {
        const forme = profondeur === 0 ? acte.paragraphes[duCorps++] : acte.paragraphesDeTableau[deTableau++];
        if (forme === undefined) {
          throw new LectureDeFormeImpossible(
            `lectureAvecForme: the document walk found more ${profondeur === 0 ? 'body' : 'table'} paragraphs than lireLaForme resolved `
            + `(${acte.paragraphes.length} body, ${acte.paragraphesDeTableau.length} table). This is a defect of lectureAvecForme.ts: its walk must mirror paragraphesDansLOrdre. Report it with this message.`,
          );
        }
        rendreLeParagraphe(candidat, forme, profondeur);
      } else if (isWord(candidat, 'tbl')) {
        rendreLeTableau(candidat, profondeur);
      } else {
        parcourir(candidat, profondeur);
      }
    }
  }
  parcourir(corps, 0);
  if (duCorps !== acte.paragraphes.length || deTableau !== acte.paragraphesDeTableau.length) {
    throw new LectureDeFormeImpossible(
      `lectureAvecForme: the document walk matched ${duCorps} body and ${deTableau} table paragraphs, lireLaForme resolved `
      + `${acte.paragraphes.length} and ${acte.paragraphesDeTableau.length}. This is a defect of lectureAvecForme.ts: its walk must mirror paragraphesDansLOrdre. Report it with this message.`,
    );
  }
  const dernierGroupe = groupes.at(-1);
  const legende = options.identifiants === true ? LEGENDE_AVEC_IDENTIFIANTS : options.editeur === 'word' ? LEGENDE_POUR_WORD : LEGENDE_DE_LA_LECTURE;
  const enTete = [...legende, `Document : ${pageEnMots(acte.sections[0])}.`];
  if (dernierGroupe === undefined) {
    if (options.depuis !== undefined && options.depuis !== 1) throw new RangHorsDuDocument(options.depuis, 0);
    return { texte: [...enTete, '', ...enAttente, '(aucun paragraphe non vide)'].join('\n'), totalParagraphes: 0, premier: 0, dernier: 0, suivant: null, ...(options.identifiants === true ? { identifiantsDuCorps: [] } : {}) };
  }
  dernierGroupe.lignes.push(...enAttente);

  const depuis = options.depuis ?? 1;
  if (!Number.isInteger(depuis) || depuis < 1 || depuis > groupes.length) {
    throw new RangHorsDuDocument(depuis, groupes.length);
  }
  const tailleMax = options.tailleMax ?? TAILLE_DE_PAGE_DE_LECTURE;
  const lignes: string[] = [];
  const identifiantsDuCorps: string[] = [];
  let taille = 0;
  let dernier = depuis - 1;
  for (const groupe of groupes.slice(depuis - 1)) {
    const poids = groupe.lignes.reduce((somme, ligne) => somme + ligne.length + 1, 0);
    if (dernier >= depuis && taille + poids > tailleMax) break;
    lignes.push(...groupe.lignes);
    if (groupe.identifiantDuCorps !== null) identifiantsDuCorps.push(groupe.identifiantDuCorps);
    taille += poids;
    dernier = groupe.rang;
  }
  const suivant = dernier < groupes.length ? dernier + 1 : null;
  return {
    texte: [...enTete, `Paragraphes ${depuis} à ${dernier} sur ${groupes.length}.${suivant === null ? '' : ` La suite commence au paragraphe ${suivant}.`}`, '', ...lignes].join('\n'),
    totalParagraphes: groupes.length,
    premier: depuis,
    dernier,
    suivant,
    ...(options.identifiants === true ? { identifiantsDuCorps } : {}),
  };
}
